/////////////////////////////////////////////////////////////////////////////////////////////////////////
// 화일명 : pgmAuthUtil.ts
// 설명   : 화면 탭/팝업을 열기 전 권한 확인(/com/apiPgmAuth) - 2026-09-28
//         - 서버의 화면 권한 검사(menuAuthGuard)와 같은 기준으로 OK / DENIED / NOT_REGISTERED를 받음.
//           DENIED면 화면 대신 403(권한 없음) 페이지를 보여줘서, 화면 파일이 없는 404와 구분함.
//         - 같은 사용자·프로그램의 OK/NOT_REGISTERED만 1분 동안 재사용. DENIED는 기억하지 않음 - 권한을 부여한 뒤
//           다시 열거나 403 화면의 [다시 확인]을 누르면 바로 반영(서버 캐시는 권한 저장 시 비워짐). 조회 실패 시에는
//           막지 않고 화면을 열어줌 - 실제 데이터는 서버 API 검사(403)가 계속 지킴.
/////////////////////////////////////////////////////////////////////////////////////////////////////////
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { getUserInfo } from './userUtils';

export type PgmAuthResult = 'OK' | 'DENIED' | 'NOT_REGISTERED' | 'MODULE_DENIED';

const CACHE_MS = 60 * 1000;
const cache = new Map<string, { at: number; p: Promise<PgmAuthResult> }>();

/** 프로그램ID 형식(예: H2001R1, Z2005M1)이 아니면 검사 대상 아님 */
export const isPgmId = (pgmId?: string): boolean => /^[A-Z][A-Z0-9]\d{3}[A-Z]\d$/i.test(String(pgmId || ''));

export function checkPgmAuth(pgmId: string): Promise<PgmAuthResult> {
  const pgm = String(pgmId || '').trim().toUpperCase();
  if (!isPgmId(pgm)) return Promise.resolve('NOT_REGISTERED');
  const key = `${getUserInfo()?.userId || ''}:${pgm}`;
  const hit = cache.get(key);
  if (hit && Date.now() - hit.at < CACHE_MS) return hit.p;
  const p = axios.post(`${API_BASE_URL}/com/apiPgmAuth`, { pgmId: pgm })
    .then(res => {
      const r = (['OK', 'DENIED', 'NOT_REGISTERED', 'MODULE_DENIED'].includes(res?.data?.rsResults) ? res.data.rsResults : 'OK') as PgmAuthResult;
      if (r === 'DENIED' || r === 'MODULE_DENIED') cache.delete(key);   // 권한 없음/모듈 미보유는 기억하지 않음(바뀐 뒤 바로 반영)
      return r;
    })
    .catch(() => { cache.delete(key); return 'OK' as PgmAuthResult; });
  cache.set(key, { at: Date.now(), p });
  return p;
}
